<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
 formId: {
 type: [Number, String],
 required: true,
 },
 embedToken: {
 type: String,
 default: '',
 },
 form: {
 type: Object,
 default: null,
 },
});

const emit = defineEmits(['close']);

const activeTab = ref('link');
const embedMethod = ref('script');
const copiedLink = ref(false);
const copiedCode = ref(false);

const baseUrl = computed(() => window.location.origin);
const token = computed(
 () => props.embedToken || props.form?.embed_token || ''
);
const slug = computed(() => props.form?.slug || '');
// Use slug for public URLs (prettier), fall back to token
const publicIdentifier = computed(() => slug.value || token.value);
const formPageUrl = computed(
 () => `${baseUrl.value}/f/${publicIdentifier.value}`
);

const embedCode = computed(() => {
 if (!token.value) return '';
 // Embed script uses token internally (container ID), but loads via slug for prettier URL
 const embedId = publicIdentifier.value;
 return `<!-- App Smart Form Embed -->
<div id="smart-form-${token.value}"></div>
<script src="${baseUrl.value}/f/${embedId}/embed" async><\/script>`;
});

const iframeCode = computed(() => {
 if (!publicIdentifier.value) return '';
 return `<!-- App Smart Form (iFrame) -->
<!-- IMPORTANTE: Este metodo NAO captura UTMs automaticamente. -->
<!-- Use o metodo Script (recomendado) para tracking automatico. -->
<iframe
 src="${formPageUrl.value}"
 width="100%"
 height="600"
 frameborder="0"
 style="border: none; max-width: 640px;"
 title="${props.form?.name || 'Formulario'}"
></iframe>`;
});

const trackingFieldsCount = computed(() => {
 const fields = props.form?.form_fields || props.form?.fields || [];
 return fields.filter(f => f.is_tracking || f.settings?.is_tracking).length;
});

const hasConversionIdentifier = computed(() => {
 return !!(
 props.form?.conversion_identifier ||
 props.form?.settings?.conversion_identifier
 );
});

const linkSteps = [
 {
 title: 'Publique o formulario',
 desc: 'Clique em "Publicar" no editor para ativar o formulario.',
 },
 {
 title: 'Copie o link abaixo',
 desc: 'Use o botao Copiar para copiar o link do formulario.',
 },
 {
 title: 'Compartilhe o link',
 desc: 'Envie por email, WhatsApp, redes sociais, anuncios, etc.',
 },
 {
 title: 'Pronto!',
 desc: 'UTMs e click IDs da URL sao capturados automaticamente.',
 },
];

const embedSteps = [
 {
 title: 'Publique o formulario',
 desc: 'O formulario precisa estar publicado para funcionar no seu site.',
 },
 {
 title: 'Cole o codigo no seu site',
 desc: 'Adicione o codigo no HTML da pagina onde o formulario deve aparecer.',
 },
 {
 title: 'Tracking automatico',
 desc: 'O script captura UTMs e click IDs da URL do visitante automaticamente.',
 },
];

async function copyToClipboard(text, type) {
 try {
 await navigator.clipboard.writeText(text);
 const ref = { link: copiedLink, code: copiedCode }[type];
 if (ref) {
 ref.value = true;
 setTimeout(() => {
 ref.value = false;
 }, 2000);
 }
 } catch {
 // Clipboard API not available
 }
}
</script>

<template>
 <Teleport to="body">
 <div class="fixed inset-0 z-[9999] flex items-center justify-center">
 <div class="absolute inset-0 bg-black/40" @click="$emit('close')" />

 <div
 class="relative bg-n-solid-1 rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col"
 >
 <!-- Header -->
 <div
 class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0"
 >
 <div class="flex items-center gap-2">
 <span
 class="i-lucide-share-2 size-5 text-woot-600 dark:text-woot-400"
 />
 <h3
 class="text-base font-semibold text-n-slate-12"
 >
 Publicar e Compartilhar
 </h3>
 </div>
 <button
 class="flex items-center justify-center size-8 rounded-lg text-n-slate-9 hover:text-n-slate-11 hover:bg-n-alpha-1 dark:hover:text-n-slate-7 transition-colors"
 @click="$emit('close')"
 >
 <span class="i-lucide-x size-4" />
 </button>
 </div>

 <!-- Tabs -->
 <div
 class="flex border-b border-n-weak px-6 shrink-0"
 >
 <button
 v-for="tab in [
 { key: 'link', label: 'Link Direto', icon: 'i-lucide-link' },
 {
 key: 'embed',
 label: 'Embed no Site',
 icon: 'i-lucide-code',
 },
 {
 key: 'tracking',
 label: 'Tracking',
 icon: 'i-lucide-radar',
 },
 ]"
 :key="tab.key"
 class="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium transition-colors relative"
 :class="
 activeTab === tab.key
 ? 'text-woot-600 dark:text-woot-400'
 : 'text-n-slate-10 hover:text-n-slate-12'
 "
 @click="activeTab = tab.key"
 >
 <span :class="tab.icon" class="size-3.5" />
 {{ tab.label }}
 <span
 v-if="activeTab === tab.key"
 class="absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500"
 />
 </button>
 </div>

 <!-- Content -->
 <div class="px-6 py-4 overflow-y-auto flex-1">
 <!-- ===== TAB: Link Direto ===== -->
 <div v-if="activeTab === 'link'">
 <div v-if="token" class="space-y-4">
 <!-- Steps -->
 <div class="space-y-3">
 <div
 v-for="(step, i) in linkSteps"
 :key="i"
 class="flex items-start gap-3"
 >
 <div
 class="flex items-center justify-center size-6 rounded-full text-xs font-bold shrink-0"
 :class="
 i === 3
 ? 'bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400'
 : 'bg-woot-100 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400'
 "
 >
 <span
 v-if="i === 3"
 class="i-lucide-check size-3.5"
 />
 <template v-else>{{ i + 1 }}</template>
 </div>
 <div>
 <p
 class="text-sm font-medium text-n-slate-12"
 >
 {{ step.title }}
 </p>
 <p
 class="text-xs text-n-slate-10"
 >
 {{ step.desc }}
 </p>
 </div>
 </div>
 </div>

 <!-- URL Box -->
 <div class="relative">
 <div
 class="flex items-center gap-2 bg-n-slate-2 border border-n-weak rounded-lg p-3"
 >
 <span
 class="i-lucide-link size-4 text-n-slate-9 shrink-0"
 />
 <span
 class="text-sm font-mono text-woot-600 dark:text-woot-400 break-all select-all"
 >
 {{ formPageUrl }}
 </span>
 </div>
 <button
 class="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors"
 :class="
 copiedLink
 ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400'
 : 'bg-n-solid-1 border border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
 "
 @click="copyToClipboard(formPageUrl, 'link')"
 >
 <span
 :class="
 copiedLink ? 'i-lucide-check' : 'i-lucide-copy'
 "
 class="size-3"
 />
 {{ copiedLink ? 'Copiado!' : 'Copiar link' }}
 </button>
 </div>

 <!-- Tip: UTMs -->
 <div
 class="flex items-start gap-2 px-3 py-2.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg"
 >
 <span
 class="i-lucide-lightbulb size-3.5 text-blue-500 shrink-0 mt-0.5"
 />
 <p
 class="text-[11px] text-blue-700 dark:text-blue-400 leading-relaxed"
 >
 Adicione UTMs ao link para rastrear a origem:
 <code
 class="font-mono bg-blue-100 dark:bg-blue-800/30 px-1 rounded"
 >
 {{ formPageUrl }}?utm_source=google&utm_medium=cpc
 </code>
 </p>
 </div>
 </div>

 <!-- Not published -->
 <div v-else class="space-y-3">
 <div
 class="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 rounded-lg"
 >
 <span
 class="i-lucide-alert-triangle size-4 text-amber-500 shrink-0"
 />
 <p class="text-sm text-amber-700 dark:text-amber-400">
 Publique o formulario primeiro para gerar o link de
 compartilhamento.
 </p>
 </div>
 </div>
 </div>

 <!-- ===== TAB: Embed no Site ===== -->
 <div v-if="activeTab === 'embed'">
 <div v-if="token" class="space-y-4">
 <!-- Steps -->
 <div class="space-y-3">
 <div
 v-for="(step, i) in embedSteps"
 :key="i"
 class="flex items-start gap-3"
 >
 <div
 class="flex items-center justify-center size-6 rounded-full text-xs font-bold shrink-0"
 :class="
 i === 2
 ? 'bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400'
 : 'bg-woot-100 dark:bg-woot-500/10 text-woot-600 dark:text-woot-400'
 "
 >
 <span
 v-if="i === 2"
 class="i-lucide-check size-3.5"
 />
 <template v-else>{{ i + 1 }}</template>
 </div>
 <div>
 <p
 class="text-sm font-medium text-n-slate-12"
 >
 {{ step.title }}
 </p>
 <p
 class="text-xs text-n-slate-10"
 >
 {{ step.desc }}
 </p>
 </div>
 </div>
 </div>

 <!-- Method toggle -->
 <div
 class="flex gap-1 p-1 bg-n-slate-3 rounded-lg"
 >
 <button
 class="flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
 :class="
 embedMethod === 'script'
 ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
 : 'text-n-slate-10'
 "
 @click="embedMethod = 'script'"
 >
 Script (recomendado)
 </button>
 <button
 class="flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
 :class="
 embedMethod === 'iframe'
 ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
 : 'text-n-slate-10'
 "
 @click="embedMethod = 'iframe'"
 >
 iFrame
 </button>
 </div>

 <!-- Script: auto-tracking badge -->
 <div
 v-if="embedMethod === 'script'"
 class="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-800 rounded-lg"
 >
 <span
 class="i-lucide-shield-check size-4 text-emerald-600 dark:text-emerald-400 shrink-0"
 />
 <span
 class="text-xs font-medium text-emerald-700 dark:text-emerald-400"
 >
 Tracking automatico incluso — UTMs e click IDs sao
 capturados da URL do visitante.
 </span>
 </div>

 <!-- iFrame: warning -->
 <div
 v-if="embedMethod === 'iframe'"
 class="flex items-start gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 rounded-lg"
 >
 <span
 class="i-lucide-alert-triangle size-4 text-amber-500 shrink-0 mt-0.5"
 />
 <div>
 <p
 class="text-xs font-medium text-amber-700 dark:text-amber-400"
 >
 Tracking limitado com iFrame
 </p>
 <p
 class="text-[11px] text-amber-600 dark:text-amber-400/80 mt-0.5"
 >
 O iFrame nao consegue ler os parametros UTM da pagina
 do seu site. Use o metodo Script para captura
 automatica de tracking.
 </p>
 </div>
 </div>

 <!-- Code block -->
 <div class="relative">
 <pre
 class="bg-n-slate-2 border border-n-weak rounded-lg p-3 text-xs font-mono text-n-slate-11 overflow-x-auto whitespace-pre-wrap break-all"
 >{{
 embedMethod === 'script' ? embedCode : iframeCode
 }}</pre>
 <button
 class="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors"
 :class="
 copiedCode
 ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400'
 : 'bg-n-solid-1 border border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
 "
 @click="
 copyToClipboard(
 embedMethod === 'script' ? embedCode : iframeCode,
 'code'
 )
 "
 >
 <span
 :class="
 copiedCode ? 'i-lucide-check' : 'i-lucide-copy'
 "
 class="size-3"
 />
 {{ copiedCode ? 'Copiado!' : 'Copiar codigo' }}
 </button>
 </div>
 </div>

 <!-- Not published -->
 <div
 v-else
 class="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 rounded-lg"
 >
 <span
 class="i-lucide-alert-triangle size-4 text-amber-500 shrink-0"
 />
 <p class="text-sm text-amber-700 dark:text-amber-400">
 Publique o formulario primeiro para obter o codigo de
 embed.
 </p>
 </div>
 </div>

 <!-- ===== TAB: Tracking ===== -->
 <div v-if="activeTab === 'tracking'" class="space-y-4">
 <!-- Status overview -->
 <div
 class="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-800 rounded-lg"
 >
 <span
 class="i-lucide-shield-check size-4 text-emerald-600 dark:text-emerald-400 shrink-0"
 />
 <span
 class="text-xs font-medium text-emerald-700 dark:text-emerald-400"
 >
 O tracking esta configurado automaticamente neste
 formulario.
 </span>
 </div>

 <p
 class="text-xs text-n-slate-10 leading-relaxed"
 >
 Quando um visitante preenche o formulario, os seguintes
 dados sao capturados automaticamente e salvos no perfil do
 contato:
 </p>

 <!-- What is captured -->
 <div class="space-y-2">
 <!-- UTMs -->
 <div
 class="flex items-start gap-2.5 px-3 py-2 bg-n-slate-2/50 rounded-lg"
 >
 <span
 class="i-lucide-tag size-3.5 text-woot-500 shrink-0 mt-0.5"
 />
 <div>
 <p
 class="text-xs font-medium text-n-slate-12"
 >
 Parametros UTM
 </p>
 <p
 class="text-[11px] text-n-slate-10"
 >
 utm_source, utm_medium, utm_campaign, utm_term,
 utm_content
 </p>
 </div>
 </div>

 <!-- Click IDs -->
 <div
 class="flex items-start gap-2.5 px-3 py-2 bg-n-slate-2/50 rounded-lg"
 >
 <span
 class="i-lucide-mouse-pointer-click size-3.5 text-amber-500 shrink-0 mt-0.5"
 />
 <div>
 <p
 class="text-xs font-medium text-n-slate-12"
 >
 Click IDs de plataformas de anuncios
 </p>
 <p
 class="text-[11px] text-n-slate-10"
 >
 Google Ads (gclid), Facebook (fbclid), TikTok
 (ttclid), Microsoft (msclkid), LinkedIn, Twitter,
 Pinterest, Snapchat, Reddit
 </p>
 </div>
 </div>

 <!-- Page context -->
 <div
 class="flex items-start gap-2.5 px-3 py-2 bg-n-slate-2/50 rounded-lg"
 >
 <span
 class="i-lucide-globe size-3.5 text-blue-500 shrink-0 mt-0.5"
 />
 <div>
 <p
 class="text-xs font-medium text-n-slate-12"
 >
 Contexto da pagina
 </p>
 <p
 class="text-[11px] text-n-slate-10"
 >
 Landing page, referrer, Page URL
 </p>
 </div>
 </div>

 <!-- Device -->
 <div
 class="flex items-start gap-2.5 px-3 py-2 bg-n-slate-2/50 rounded-lg"
 >
 <span
 class="i-lucide-monitor size-3.5 text-n-slate-10 shrink-0 mt-0.5"
 />
 <div>
 <p
 class="text-xs font-medium text-n-slate-12"
 >
 Dispositivo
 </p>
 <p
 class="text-[11px] text-n-slate-10"
 >
 Endereco IP e User Agent (navegador/sistema
 operacional)
 </p>
 </div>
 </div>

 <!-- Conversion -->
 <div
 v-if="hasConversionIdentifier"
 class="flex items-start gap-2.5 px-3 py-2 bg-n-slate-2/50 rounded-lg"
 >
 <span
 class="i-lucide-zap size-3.5 text-emerald-500 shrink-0 mt-0.5"
 />
 <div>
 <p
 class="text-xs font-medium text-n-slate-12"
 >
 Evento de conversao
 </p>
 <p
 class="text-[11px] text-n-slate-10"
 >
 Registrado como "{{
 form?.conversion_identifier ||
 form?.settings?.conversion_identifier
 }}" na timeline do contato
 </p>
 </div>
 </div>
 </div>

 <!-- Tracking fields count -->
 <div
 class="flex items-center gap-2 px-3 py-2 bg-n-slate-2/50 rounded-lg"
 >
 <span
 class="i-lucide-layers size-3.5 text-n-slate-9 shrink-0"
 />
 <span
 class="text-xs text-n-slate-11"
 >
 <strong>{{ trackingFieldsCount }}</strong> campos de
 tracking configurados neste formulario
 </span>
 </div>

 <!-- Where data appears -->
 <div
 class="flex items-start gap-2 px-3 py-2.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-lg"
 >
 <span
 class="i-lucide-info size-3.5 text-blue-500 shrink-0 mt-0.5"
 />
 <p
 class="text-[11px] text-blue-700 dark:text-blue-400 leading-relaxed"
 >
 Os dados de tracking aparecem no perfil do contato, na
 seção <strong>Atribuição / UTM</strong>, com primeiro
 contato e último contato. Cada evento também aparece na
 <strong>timeline de atividades</strong> do contato.
 </p>
 </div>

 <!-- Script method recommendation -->
 <div
 class="flex items-start gap-2 px-3 py-2.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-800 rounded-lg"
 >
 <span
 class="i-lucide-lightbulb size-3.5 text-amber-500 shrink-0 mt-0.5"
 />
 <p
 class="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed"
 >
 <strong>Importante:</strong> Se voce esta usando o
 formulario via embed no site, use o metodo
 <strong>Script</strong> (na aba Embed) para garantir que
 os parametros UTM da pagina do seu site sejam repassados
 ao formulario. O metodo iFrame nao consegue ler a URL da
 pagina pai.
 </p>
 </div>
 </div>
 </div>

 <!-- Footer -->
 <div
 class="flex items-center justify-end px-6 py-3 border-t border-n-weak shrink-0"
 >
 <button
 class="px-4 py-2 text-sm font-medium text-n-slate-11 bg-n-slate-3 rounded-lg hover:bg-n-alpha-2 transition-colors"
 @click="$emit('close')"
 >
 Fechar
 </button>
 </div>
 </div>
 </div>
 </Teleport>
</template>
